<template>
	<textarea
		ref="inputField"
		:class="classes"
		:disabled="disabled"
		:placeholder="placeholder"
	></textarea>
</template>

<script>
const validResizeValues = new Set( [ "none", "both", "horizontal", "vertical", ] );

export default {
	name: "TeamingTextarea",
	props: {
		borderless: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		placeholder: {
			type: String,
			default: "",
		},
		resize: {
			type: String,
			validator( value ) {
				return validResizeValues.has( value );
			},
			default: "none",
		},
		isValid: {
			type: Boolean,
			default: true,
		},
		focus: {
			type: Boolean,
			default: false,
		},
		selection: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isFocused: false,
		};
	},
	computed: {
		classes() {
			return {
				"teaming-text-area": true,
				"teaming-text-area--invalid": !this.isValid,
				"teaming-text-area--resize-both": this.resize === "both",
				"teaming-text-area--resize-horizontal": this.resize === "horizontal",
				"teaming-text-area--resize-vertical": this.resize === "vertical",
				"teaming-text-area--resize-none": this.resize === "none",
				"teaming-text-area--borderless": this.borderless,
				"teaming-text-area--focused--valid": this.isValid && this.isFocused,
				"teaming-text-area--focused--invalid": !this.isValid && this.isFocused,
			};
		},
	},
	watch: {
		focus( isFocused, wasFocused ) {
			if ( isFocused && !wasFocused ) {
				this.$refs.inputField.focus();
			}
		},
	},
	mounted() {
		if ( this.focus ) {
			this.$refs.inputField.focus();
		}

		// @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
		if ( this.selection.length ) {
			if ( this.selection.length === 1 ) {
				const [ start, ] = this.selection;
				this.$refs.inputField.setSelectionRange( start, start );
			}
			if ( this.selection.length === 2 ) {
				const [ start, end, ] = this.selection;
				this.$refs.inputField.setSelectionRange( start, end );
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.teaming-text-area {
	background: $textarea-background-color;
	color: $textarea-font-color;
	box-sizing: border-box;
	padding: .625rem .75rem .625rem .75rem;
	border-radius: .5rem;
	outline: none;

	&:not(.teaming-text-area--invalid) {
		border: 0.0625rem solid $textarea-border-color;
	}

	&::placeholder {
		color: $textarea-placeholder-font-color;
	}

	&:hover:not(.teaming-text-area--invalid) {
		border: 0.0625rem solid $textarea-border-color--hover;
	}

	&:focus:not(.teaming-text-area--invalid) {
		border-radius: 0.25rem;
		border: 0.09375rem solid $textarea-border-color--focus;
		box-shadow: 0rem 0rem 0rem 0.1875rem $textarea-border-shadow-color--focus;
	}
}

.teaming-text-area--invalid {
	border: 1.5px solid $textarea-invalid-border-color;

	&:hover {
		border: 0.0625rem solid $textarea-invalid-hover-border-color;
	}

	&:focus {
		border-radius: 0.25rem;
		border: 0.09375rem solid $textarea-invalid-focus-border-color;
		box-shadow: 0rem 0rem 0rem 0.1875rem $textarea-invalid-focus-box-shadow-color;
	}
}

.teaming-text-area--resize-both {
	resize: both;
}

.teaming-text-area--resize-horizontal {
	resize: horizontal;
}

.teaming-text-area--resize-vertical {
	resize: vertical;
}

.teaming-text-area--resize-none {
	resize: none;
}

.teaming-text-area--borderless {
	border: none;
}

.teaming-text-area--focused--invalid {
	border: 0.0625rem solid $textarea-invalid-hover-border-color;
	border-radius: 0.5rem;
	box-shadow: 0 0 0 .1875rem $textarea-invalid-focus-box-shadow-color;
}

.teaming-text-area--focused--valid {
	border: 0.0625rem solid $textarea-border-color--hover;
	border-radius: 0.5rem;
	box-shadow: 0 0 0 .1875rem $textarea-border-shadow-color--focus;
}
</style>
